import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCRMCountByStatus, useCRMMain } from '../../hooks';
import { useCRMAgGridColumns } from '../../hooks/useAgGridColumns';
import { useCrmStatus, useModifyOrderStatus } from '../../hooks/useCrmStatus';
import { useCancelOrder, useConfirmOrder, useUncancelOrder } from '../../hooks/useOrderActions';
import { useModifyOrderTags, useTags } from '../../hooks/useTags';
import {
  useCancelOrdersBulk,
  useConfirmOrdersBulk,
  useUncancelOrdersBulk,
  useModifyOrderStatusBulk,
  useAddTagsToOrdersBulk,
} from '../../hooks/useBulkOrderActions';
import { CRMModel } from '../../models/CRM';
import { Breadcrumb, CRMAgGridTable, type CRMAgGridTableRef } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { CRMStatusToolbar, CRMSelectionToolbar, FiltersSection } from '../interaction';
import {
  CustomerOrdersModal,
  CustomerOrdersModalRef,
  NotesModal,
  NotesModalRef,
  ProductsModal,
  TagsModal,
  TagsModalRef,
} from '../modals';

import { faCircleCheck, faCircleXmark, faRotateLeft } from '@fortawesome/pro-light-svg-icons';

const isFixedCrmStatus = (crmStatus: any): boolean => {
  const current = Array.isArray(crmStatus) ? crmStatus[0] : crmStatus;
  return current && current.team_id == null;
};

const isConfirmedCrmStatus = (crmStatus: any): boolean => {
  const current = Array.isArray(crmStatus) ? crmStatus[0] : crmStatus;
  if (!current || !current.name) return false;
  const name = String(current.name).toLowerCase().trim();
  return name === 'confirmado' || name === 'confirmed' || name.includes('confirmad');
};

const isCancelledCrmStatus = (crmStatus: any): boolean => {
  const current = Array.isArray(crmStatus) ? crmStatus[0] : crmStatus;
  if (!current || !current.name) return false;
  const name = String(current.name).toLowerCase().trim();
  return name === 'cancelado' || name === 'cancelled' || name.includes('cancelad');
};

const canManageCrmStatus = (crmStatus: any): boolean => {
  const current = Array.isArray(crmStatus) ? crmStatus[0] : crmStatus;
  return current?.team_id != null;
};

export const Main = () => {
  const { t } = useTranslation('crmModule');
  const navigate = useNavigate();

  const {
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    allTotal,
    sorting,
    selectedColumns,
    selectedOrders,
    ordersViewProps,
    hasActiveFilters,
    fixedColumns,
    searchTerm,
    teamIds,
    phoneSearchTerm,
    statusId,
    shopIds,
    tagId,
    shippingCountryCodes,
    paymentMethodIds,
    productIds,
    dateFrom,
    dateTo,
    filters,
    handleSearch,
    handleTeamIdsChange,
    handlePhoneSearchChange,
    handleStatusChange,
    handleShopIdsChange,
    handleTagChange,
    handleShippingCountryCodesChange,
    handlePaymentMethodChange,
    handleProductIdsChange,
    handleDateRangeChange,
    handleColumnsChange,
    handleSortingChange,
    handleClearFilters,
    handleSelectionChanged,
    handleClearSelection,
  } = useCRMMain();

  const queryClient = useQueryClient();

  const { toast } = useToast();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notesModalRef = useRef<NotesModalRef>(null);
  const tagsModalRef = useRef<TagsModalRef>(null);
  const customerOrdersModalRef = useRef<CustomerOrdersModalRef>(null);
  const removeTagConfirmRef = useRef<ConfirmModalRef>(null);
  const pendingTagRemoveRef = useRef<{ orderId: number; tagId: number } | null>(null);
  const tableRef = useRef<CRMAgGridTableRef>(null);

  const { statusList } = useCrmStatus();
  const { tags: tagList } = useTags({ per_page: 100 });

  // Bulk action mutations
  const cancelOrdersBulk = useCancelOrdersBulk();
  const confirmOrdersBulk = useConfirmOrdersBulk();
  const uncancelOrdersBulk = useUncancelOrdersBulk();
  const modifyOrderStatusBulk = useModifyOrderStatusBulk();
  const addTagsToOrdersBulk = useAddTagsToOrdersBulk();

  const isAnyBulkActionPending =
    cancelOrdersBulk.isPending ||
    confirmOrdersBulk.isPending ||
    uncancelOrdersBulk.isPending ||
    modifyOrderStatusBulk.isPending ||
    addTagsToOrdersBulk.isPending;

  // Compute action-eligible counts from selected orders using CRM status names
  const cancelableCount = useMemo(
    () =>
      selectedOrders.filter((o) => {
        const fixed = isFixedCrmStatus(o.crm_status);
        const cancelled = isCancelledCrmStatus(o.crm_status);
        return !fixed && !cancelled;
      }).length,
    [selectedOrders],
  );
  const confirmableCount = useMemo(
    () =>
      selectedOrders.filter((o) => {
        const fixed = isFixedCrmStatus(o.crm_status);
        const confirmed = isConfirmedCrmStatus(o.crm_status);
        const cancelled = isCancelledCrmStatus(o.crm_status);
        return !fixed && !confirmed && !cancelled;
      }).length,
    [selectedOrders],
  );
  const uncancelableCount = useMemo(
    () => selectedOrders.filter((o) => isCancelledCrmStatus(o.crm_status)).length,
    [selectedOrders],
  );

  const clearSelection = useCallback(() => {
    tableRef.current?.deselectAll();
    handleClearSelection();
  }, [handleClearSelection]);

  const handleBulkCancel = async () => {
    const ids = selectedOrders
      .filter((o) => {
        const fixed = isFixedCrmStatus(o.crm_status);
        const cancelled = isCancelledCrmStatus(o.crm_status);
        return !fixed && !cancelled;
      })
      .map((o) => o.id);
    try {
      await cancelOrdersBulk.mutateAsync(ids);
      toast({ title: t('selection.cancelSuccess', { count: ids.length }) });
      clearSelection();
    } catch {
      toast({ title: t('selection.cancelError', 'Error al cancelar'), variant: 'destructive' });
    }
  };

  const handleBulkConfirm = async () => {
    const ids = selectedOrders
      .filter((o) => {
        const fixed = isFixedCrmStatus(o.crm_status);
        const confirmed = isConfirmedCrmStatus(o.crm_status);
        const cancelled = isCancelledCrmStatus(o.crm_status);
        return !fixed && !confirmed && !cancelled;
      })
      .map((o) => o.id);
    try {
      await confirmOrdersBulk.mutateAsync(ids);
      toast({ title: t('selection.confirmSuccess', { count: ids.length }) });
      clearSelection();
    } catch {
      toast({ title: t('selection.confirmError', 'Error al confirmar'), variant: 'destructive' });
    }
  };

  const handleBulkUncancel = async () => {
    const ids = selectedOrders.filter((o) => isCancelledCrmStatus(o.crm_status)).map((o) => o.id);
    try {
      await uncancelOrdersBulk.mutateAsync(ids);
      toast({ title: t('selection.uncancelSuccess', { count: ids.length }) });
      clearSelection();
    } catch {
      toast({ title: t('selection.uncancelError', 'Error al descancelar'), variant: 'destructive' });
    }
  };

  const handleBulkChangeStatus = async (crmStatusId: number | null) => {
    const ids = selectedOrders.map((o) => o.id);
    try {
      await modifyOrderStatusBulk.mutateAsync({ orderIds: ids, crmStatusId });
      toast({ title: t('selection.statusSuccess', { count: ids.length }) });
      clearSelection();
    } catch {
      toast({ title: t('selection.statusError', 'Error al cambiar estado'), variant: 'destructive' });
    }
  };

  const handleBulkAddTags = async (tagIds: number[]) => {
    const ids = selectedOrders.map((o) => o.id);
    try {
      await addTagsToOrdersBulk.mutateAsync({ orderIds: ids, tagIds });
      toast({ title: t('selection.tagsSuccess', { count: ids.length }) });
      clearSelection();
    } catch {
      toast({ title: t('selection.tagsError', 'Error al añadir etiquetas'), variant: 'destructive' });
    }
  };

  // Determinar el tab activo basado en statusId
  const activeTab = useMemo(() => {
    if (statusId === undefined) {
      return 'all';
    }
    if (statusId === null) {
      return 'no-status';
    }
    return `status_${statusId}`;
  }, [statusId]);

  // Función para cambiar el tab activo
  const setActiveTab = useCallback(
    (key: string) => {
      if (key === 'all') {
        handleStatusChange(undefined);
      } else if (key === 'no-status') {
        handleStatusChange(null);
      } else {
        // Extraer el statusId del key (formato: "status_123")
        const statusIdFromKey = parseInt(key.replace('status_', ''), 10);
        if (!isNaN(statusIdFromKey)) {
          handleStatusChange(statusIdFromKey);
        }
      }
    },
    [handleStatusChange],
  );

  // Hook para obtener contadores de pedidos CRM por estado
  const {
    counts: statusCounts,
    total: statusTotal,
    isLoading: isLoadingCounts,
  } = useCRMCountByStatus({
    search: searchTerm,
    filters: {
      team_ids: (teamIds?.length ?? 0) > 0 ? teamIds.join(',') : undefined,
      shop_ids: (shopIds?.length ?? 0) > 0 ? shopIds.join(',') : undefined,
      tag_id: tagId,
      shipping_country_codes: (shippingCountryCodes?.length ?? 0) > 0 ? shippingCountryCodes.join(',') : undefined,
      payment_method_ids: (paymentMethodIds?.length ?? 0) > 0 ? paymentMethodIds.join(',') : undefined,
      product_ids: (productIds?.length ?? 0) > 0 ? productIds.join(',') : undefined,
      date_from: dateFrom,
      date_to: dateTo,
    },
  });
  const modifyOrderStatusMutation = useModifyOrderStatus();
  const confirmOrderMutation = useConfirmOrder();
  const cancelOrderMutation = useCancelOrder();
  const uncancelOrderMutation = useUncancelOrder();
  const modifyOrderTagsMutation = useModifyOrderTags();

  // Refs para modales de confirmación de acciones individuales
  const confirmOrderModalRef = useRef<ConfirmModalRef>(null);
  const cancelOrderModalRef = useRef<ConfirmModalRef>(null);
  const uncancelOrderModalRef = useRef<ConfirmModalRef>(null);

  // Handlers con modal + mutation + toast para acciones individuales
  const handleConfirmAction = (orderId: number) => {
    confirmOrderModalRef.current?.open(
      async () => {
        await confirmOrderMutation.mutateAsync(orderId);
        toast({ title: t('actions.confirmSuccess.title', 'Pedido confirmado'), variant: 'default' });
      },
      undefined,
      t('actions.confirmConfirm.title', 'Confirmar pedido'),
      t('actions.confirmConfirm.message', '¿Estás seguro de que deseas confirmar este pedido?'),
    );
  };

  const handleCancelAction = (orderId: number) => {
    cancelOrderModalRef.current?.open(
      async () => {
        await cancelOrderMutation.mutateAsync(orderId);
        toast({ title: t('actions.cancelSuccess.title', 'Pedido cancelado'), variant: 'default' });
      },
      undefined,
      t('actions.cancelConfirm.title', 'Cancelar pedido'),
      t('actions.cancelConfirm.message', '¿Estás seguro de que deseas cancelar este pedido?'),
    );
  };

  const handleUncancelAction = (orderId: number) => {
    uncancelOrderModalRef.current?.open(
      async () => {
        await uncancelOrderMutation.mutateAsync(orderId);
        toast({ title: t('actions.uncancelSuccess.title', 'Pedido descancelado'), variant: 'default' });
      },
      undefined,
      t('actions.uncancelConfirm.title', 'Descancelar pedido'),
      t('actions.uncancelConfirm.message', '¿Estás seguro de que deseas descancelar este pedido?'),
    );
  };

  // Armar acciones disponibles por orden (siempre devuelve las 3, disabled si no aplica)
  const getAvailableActions = (order: CRMModel) => {
    const currentStatus = Array.isArray(order.crm_status) ? order.crm_status[0] : order.crm_status;
    const isFixedStatus = canManageCrmStatus(currentStatus);
    const cancelled = isCancelledCrmStatus(order.crm_status);
    const confirmed = isConfirmedCrmStatus(order.crm_status);

    return [
      {
        id: 'confirm',
        label: t('actions.confirm'),
        enabled: isFixedStatus && !confirmed && !cancelled,
        icon: faCircleCheck,
        onClick: () => handleConfirmAction(Number(order.id)),
      },
      {
        id: 'cancel',
        label: t('actions.cancel'),
        enabled: isFixedStatus && !cancelled,
        icon: faCircleXmark,
        onClick: () => handleCancelAction(Number(order.id)),
      },
      {
        id: 'uncancel',
        label: t('actions.uncancel'),
        enabled: cancelled,
        icon: faRotateLeft,
        onClick: () => handleUncancelAction(Number(order.id)),
      },
    ];
  };

  // Detectar si alguna acción está en progreso (confirmar, cancelar)
  const isAnyActionInProgress = confirmOrderMutation.isPending || cancelOrderMutation.isPending;

  const handleStatusChangeOrder = useCallback(
    async (orderId: number, crmStatusId: number | null) => {
      try {
        await queryClient.cancelQueries({ queryKey: ['crm-orders'] });
        await queryClient.cancelQueries({ queryKey: ['crm-orders-count-by-status'] });

        const previousOrdersQueries = queryClient.getQueriesData({ queryKey: ['crm-orders'] });

        // Buscar el estado completo en la lista
        const newStatusObj = statusList.find((s) => s.id === crmStatusId);

        const newStatusMetadata = newStatusObj
          ? {
              ...newStatusObj,
              managed_at: new Date().toISOString(),
              managed_by: { id: 0, name: 'Yo', email: '' },
            }
          : null;

        const newCrmStatus = newStatusMetadata ? [newStatusMetadata] : [];

        // 1. Encontrar el pedido y su estado anterior
        let orderToMove: CRMModel | null = null;
        let oldCrmStatusId: number | null = null;

        previousOrdersQueries.forEach(([, data]: any) => {
          if (!orderToMove && data?.pages) {
            data.pages.forEach((page: any) => {
              const found = page.orders.find((o: any) => Number(o.id) === Number(orderId));
              if (found) {
                orderToMove = { ...found };
                const status = Array.isArray(found.crm_status) ? found.crm_status[0] : found.crm_status;
                oldCrmStatusId = status?.id ?? null;
              }
            });
          }
        });

        if (!orderToMove) return;

        const updatedOrder = {
          ...(orderToMove as any),
          crm_status: newCrmStatus,
          crm_status_id: crmStatusId,
        };

        // 2. Ejecutar mutación
        await modifyOrderStatusMutation.mutateAsync({ order_id: orderId, crm_status_id: crmStatusId });

        // 3. Actualizar localmente SIN refetch todas las queries coincidentes (Sincronización de pestañas)
        queryClient.setQueriesData({ queryKey: ['crm-orders'] }, (old: any, query: any) => {
          if (!old || !Array.isArray(old.pages)) return old;

          const queryFilters = (query?.queryKey && query.queryKey[1]) as Record<string, any> | undefined;
          const queryCrmStatusId = queryFilters?.crm_status_id;

          let totalChange = 0;
          const newPages = old.pages.map((page: any) => {
            if (!page || !Array.isArray(page.orders)) return page;
            const isOrderInPage = page.orders.some((o: any) => Number(o?.id) === Number(orderId));

            if (isOrderInPage) {
              // Si la query es del estado anterior (y no es el nuevo), quitar el pedido
              if (
                queryCrmStatusId !== undefined &&
                queryCrmStatusId !== crmStatusId &&
                queryCrmStatusId === oldCrmStatusId
              ) {
                totalChange--;
                return {
                  ...page,
                  orders: page.orders.filter((o: any) => Number(o?.id) !== Number(orderId)),
                };
              }
              // En cualquier otro caso (incluyendo "Todos"), actualizar el pedido
              return {
                ...page,
                orders: page.orders.map((o: any) => (Number(o?.id) === Number(orderId) ? updatedOrder : o)),
              };
            }

            // Si el pedido NO está en esta página pero esta query es la del nuevo estado, añadirlo
            if (!isOrderInPage && queryCrmStatusId === crmStatusId && crmStatusId !== null) {
              const isFirstPage = old.pages[0] === page;
              if (isFirstPage) {
                totalChange++;
                return {
                  ...page,
                  orders: [updatedOrder, ...page.orders],
                };
              }
            }

            return page;
          });

          // Si el total cambió, actualizarlo en la primera página (useCRM lo lee de ahí)
          if (totalChange !== 0 && newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              total: Math.max(0, (newPages[0].total || 0) + totalChange),
            };
          }

          return Object.assign({}, old, { pages: newPages });
        });

        // 4. Actualizar contadores
        queryClient.setQueriesData({ queryKey: ['crm-orders-count-by-status'] }, (oldCounts: any) => {
          if (!oldCounts) return oldCounts;
          const newCounts = { ...(oldCounts as object) } as Record<string, number>;
          if (oldCrmStatusId !== null && newCounts[oldCrmStatusId] > 0) {
            newCounts[oldCrmStatusId]--;
          }
          if (crmStatusId !== null) {
            newCounts[crmStatusId] = (newCounts[crmStatusId] || 0) + 1;
          }
          return newCounts;
        });

        toast({
          title: t('table.statusChangeSuccess', 'Estado actualizado'),
          description: crmStatusId
            ? t('table.statusChangeSuccessDescription', 'El estado del pedido se ha actualizado correctamente.')
            : t('table.statusRemovedDescription', 'Se ha quitado el estado del pedido.'),
          variant: 'default',
        });
      } catch (error: any) {
        toast({
          title: t('table.statusChangeError', 'Error al cambiar estado'),
          description: error?.response?.data?.message ?? t('toasts.error_title', 'Error'),
          variant: 'destructive',
        });
      }
    },
    [modifyOrderStatusMutation, statusList, queryClient, t, toast],
  );

  const handleTagRemove = useCallback(
    async (orderId: number, tagId: number) => {
      try {
        await modifyOrderTagsMutation.mutateAsync({
          order_id: orderId,
          detach_tags: [tagId],
        });

        // Actualizar localmente sin refetch
        queryClient.setQueryData(['crm-orders', filters], (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              orders: page.orders.map((order: CRMModel) =>
                order.id === orderId ? { ...order, tags: (order.tags || []).filter((tag) => tag.id !== tagId) } : order,
              ),
            })),
          };
        });

        toast({
          title: t('tags.removeSuccess.title', 'Etiqueta eliminada'),
          description: t('tags.removeSuccess.description', 'La etiqueta se ha quitado del pedido.'),
          variant: 'default',
        });
      } catch (error: any) {
        toast({
          title: t('tags.removeError.title', 'Error al eliminar etiqueta'),
          description:
            error?.response?.data?.message || error?.message || t('tags.removeError.description', 'Error inesperado.'),
          variant: 'destructive',
        });
      }
    },
    [modifyOrderTagsMutation, queryClient, filters, t, toast],
  );

  const openRemoveTagConfirm = useCallback(
    (orderId: number, tagId: number) => {
      pendingTagRemoveRef.current = { orderId, tagId };
      removeTagConfirmRef.current?.open(
        async () => {
          const p = pendingTagRemoveRef.current;
          if (p) await handleTagRemove(p.orderId, p.tagId);
          pendingTagRemoveRef.current = null;
        },
        undefined,
        t('tags.removeConfirm.title', 'Desasignar etiqueta'),
        t('tags.removeConfirm.messageGeneric', '¿Estás seguro de que deseas desasignar esta etiqueta del pedido?'),
      );
    },
    [handleTagRemove, t],
  );

  const handleViewClick = useCallback(
    (order: CRMModel, e?: MouseEvent<Element>) => {
      if (e?.ctrlKey || e?.metaKey) {
        window.open(`/orders/crm/view/${order.id}`, '_blank');
      } else {
        navigate(`/orders/crm/view/${order.id}`);
      }
    },
    [navigate],
  );

  const handleNotesClick = useCallback((order: CRMModel) => {
    notesModalRef.current?.open(order.id);
  }, []);

  const handleTagsClick = useCallback((order: CRMModel) => {
    tagsModalRef.current?.open(order.id, order.ref, order.tags ?? []);
  }, []);

  const handleCustomerOrdersClick = useCallback((customerId: number, customerName: string) => {
    customerOrdersModalRef.current?.open(customerId, customerName);
  }, []);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const { filteredColumnDefs, tanStackColumns, productsModalRef } = useCRMAgGridColumns({
    selectedColumns,
    refetch,
    onViewClick: handleViewClick,
    onNotesClick: handleNotesClick,
    onTagsClick: handleTagsClick,
    onTagRemove: openRemoveTagConfirm,
    statusList,
    onStatusChange: handleStatusChangeOrder,
    onCustomerOrdersClick: handleCustomerOrdersClick,
    filters,
    getAvailableActions,
  });

  const selectorColumns = useMemo(() => {
    return tanStackColumns;
  }, [tanStackColumns]);

  // Envolver fetchNextPage en useCallback para mantener referencia estable
  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const renderCRM = () => {
    return (
      <CRMAgGridTable
        ref={tableRef}
        orders={ordersViewProps.orders}
        columnDefs={filteredColumnDefs}
        fetchNextPage={handleFetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        onSelectionChanged={handleSelectionChanged}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando CRM...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <Breadcrumb />
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              allColumns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={fixedColumns}
              searchTerm={searchTerm}
              teamIds={teamIds}
              phoneSearchTerm={phoneSearchTerm}
              statusId={statusId}
              shopIds={shopIds}
              tagId={tagId}
              shippingCountryCodes={shippingCountryCodes}
              paymentMethodIds={paymentMethodIds}
              productIds={productIds}
              dateFrom={dateFrom}
              dateTo={dateTo}
              sorting={sorting ?? undefined}
              statusList={statusList}
              onStatusChange={handleStatusChange}
              onShopIdsChange={handleShopIdsChange}
              onTagChange={handleTagChange}
              onShippingCountryCodesChange={handleShippingCountryCodesChange}
              onPaymentMethodChange={handlePaymentMethodChange}
              onTeamIdsChange={handleTeamIdsChange}
              onPhoneSearchChange={handlePhoneSearchChange}
              onProductIdsChange={handleProductIdsChange}
              onDateRangeChange={handleDateRangeChange}
              onClearFilters={handleClearFilters}
            />
          </div>
          <div>
            <TotalResults total={allTotal} />
          </div>
        </div>

        {/* Tabs de estados CRM */}
        {!isLoading && (
          <CRMStatusToolbar
            statusList={statusList}
            onStatusChange={handleStatusChange}
            statusCounts={statusCounts}
            total={statusTotal}
            isLoadingCounts={isLoadingCounts}
            active={activeTab}
            setActive={setActiveTab}
          />
        )}
      </div>

      {/* Lista principal: opacity y disabled cuando cualquier acción está en progreso */}
      {!isInitialLoad && (
        <div className="relative mb-4 mt-4">
          <div
            className={`transition-opacity duration-300 ${
              isAnyActionInProgress
                ? 'opacity-50 pointer-events-none'
                : isFetching && !isFetchingNextPage
                  ? 'opacity-50 pointer-events-none'
                  : total > 0
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}
          >
            {renderCRM()}
          </div>

          {/* EmptyView solo cuando no hay datos */}
          {total === 0 && (
            <div className="absolute inset-0">
              <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.PRODUCTS} />
            </div>
          )}
        </div>
      )}

      <NotesModal ref={notesModalRef} onSuccess={refetch} />
      <TagsModal ref={tagsModalRef} onSuccess={refetch} />
      <ConfirmModal ref={removeTagConfirmRef} />
      <ProductsModal ref={productsModalRef} />
      <CustomerOrdersModal ref={customerOrdersModalRef} />
      <ConfirmModal ref={confirmOrderModalRef} />
      <ConfirmModal ref={cancelOrderModalRef} />
      <ConfirmModal ref={uncancelOrderModalRef} />

      <CRMSelectionToolbar
        selectedCount={selectedOrders.length}
        cancelableCount={cancelableCount}
        confirmableCount={confirmableCount}
        uncancelableCount={uncancelableCount}
        statusList={statusList}
        tagList={tagList}
        isLoading={isAnyBulkActionPending}
        onClose={clearSelection}
        onCancelSelected={handleBulkCancel}
        onConfirmSelected={handleBulkConfirm}
        onUncancelSelected={handleBulkUncancel}
        onChangeStatus={handleBulkChangeStatus}
        onAddTags={handleBulkAddTags}
      />
    </div>
  );
};
